import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DestructiveHoldToConfirmButton } from "@src/appV2/redesign/components/DestructiveHoldToConfirmButton";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  GOLDEN_RATIO_BOTTOM_FLEX,
  GOLDEN_RATIO_TOP_FLEX,
} from "@src/appV2/redesign/utils/goldenRatioLayout";

import { RestrictionConsequencesCard } from "./components/RestrictionConsequencesCard";

interface ShiftCancelRestrictionWarningScreenProps {
  canUseCredit: boolean;
  restrictionDurationDays: number | undefined;
  isCancelling: boolean;
  onPressAndHoldToCancel: () => void;
  onKeepShift: () => void;
}

export function ShiftCancelRestrictionWarningScreen(
  props: ShiftCancelRestrictionWarningScreenProps
): JSX.Element {
  const {
    canUseCredit,
    restrictionDurationDays,
    isCancelling,
    onPressAndHoldToCancel,
    onKeepShift,
  } = props;

  useLogEffect(APP_V2_USER_EVENTS.SHIFT_CANCEL_RESTRICTION_WARNING_VIEWED);

  const subtitle = canUseCredit
    ? "Your score will reach zero even after applying your cancellation credit."
    : "When your score reaches zero or below, your account is Restricted.";

  return (
    <Stack flex={1}>
      <Box sx={{ flex: GOLDEN_RATIO_TOP_FLEX, minHeight: 0 }} />

      <Stack
        spacing={8}
        alignItems="center"
        sx={{ paddingX: 6, paddingTop: 2, textAlign: "center" }}
      >
        <CbhIcon
          type="triangle-exclamation"
          color={(theme) => theme.palette.intent?.error?.backgroundInvert}
          size="xLarge"
        />

        <Stack spacing={4} alignItems="center">
          <Text variant="h3" sx={(theme) => ({ color: theme.palette.intent?.error?.text })}>
            Your account will be Restricted
          </Text>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            {subtitle}
          </Text>
        </Stack>

        <RestrictionConsequencesCard restrictionDurationDays={restrictionDurationDays} />
      </Stack>

      <Box sx={{ flex: GOLDEN_RATIO_BOTTOM_FLEX, minHeight: 0 }} />

      <DialogFooter orientation="vertical">
        <Stack spacing={3} sx={{ width: "100%" }}>
          <DestructiveHoldToConfirmButton
            isLoading={isCancelling}
            disabled={isCancelling}
            onConfirm={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_CANCEL_RESTRICTION_WARNING_CONFIRMED);
              onPressAndHoldToCancel();
            }}
          >
            {isCancelling ? "Cancelling..." : "Press and hold to cancel & restrict account"}
          </DestructiveHoldToConfirmButton>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={isCancelling}
            sx={(theme) => ({
              color: theme.palette.text.primary,
              borderColor: theme.palette.border?.primary,
            })}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_CANCEL_RESTRICTION_WARNING_KEPT_SHIFT);
              onKeepShift();
            }}
          >
            Keep my shift
          </Button>
        </Stack>
      </DialogFooter>
    </Stack>
  );
}
